import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import OpenAI from 'openai';
import ReactMarkdown from 'react-markdown';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  colors,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import contentHelper from 'helpers/contentHelper';
import AudioPlayer from './AudioPlayer';
import useStickyState from 'hooks/useStickyState';
import { prompts } from './prompts';
import { ContentCopy } from '@mui/icons-material';
import CopyToClipboard from 'react-copy-to-clipboard';
import { fetcher } from 'data_sources/constants';

const voices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];

export default function TTS() {
  const [textInput, setTextInput] = useState('');
  const [voice, setVoice] = useState(voices[0]);
  const [fileName, setFileName] = useState<string>('');
  const [apiKey, setApiKey] = useStickyState<string>('openaiKey', '');
  const [suggestion, setSuggestion] = useState<any>();
  const [mp3Content, setMp3Content] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState<boolean>(false);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [audioGenerationKey, setAudioGenerationKey] = useState<number>(0);
  const url = `https://9uc9gz2k88.execute-api.us-east-1.amazonaws.com/dev/tts`;

  const resetDefaults = () => {
    setFileName('');
    setMp3Content('');
  };

  // Clear audio when voice changes so user knows they need to regenerate
  useEffect(() => {
    if (mp3Content) {
      // Revoke blob URL before clearing
      if (mp3Content.startsWith('blob:')) {
        URL.revokeObjectURL(mp3Content);
      }
      setMp3Content('');
      setAudioGenerationKey(0);
    }
  }, [voice]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (mp3Content && mp3Content.startsWith('blob:')) {
        URL.revokeObjectURL(mp3Content);
      }
    };
  }, [mp3Content]);

  const handleGenerate = async () => {
    // Clear existing audio immediately to show new generation is starting
    // Revoke old blob URL to prevent memory leaks
    if (mp3Content && mp3Content.startsWith('blob:')) {
      URL.revokeObjectURL(mp3Content);
    }
    setMp3Content('');

    const payload = { model: 'tts-1', voice: voice, input: textInput };
    setIsGenerating(true);
    try {
      const data = await fetcher({
        url,
        method: 'POST',
        body: payload,
        anonymousAllowed: true,
        options: {
          headers: {
            'X-Api-Key': process.env.REACT_APP_API_GATEWAY_KEY || '',
            'Content-Type': 'application/json',
          } as HeadersInit,
        },
      });
      // Handle API Gateway response structure: {statusCode: 200, body: "\"base64string\""}
      let base64Audio: string;
      if (data?.body) {
        // body is a JSON-stringified base64 string, so we need to parse it
        base64Audio = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      } else if (typeof data === 'string') {
        // Fallback: if data itself is the base64 string
        base64Audio = data;
      } else {
        throw new Error('Unexpected response format from API Gateway');
      }

      // Convert base64 to blob and create a fresh blob URL for each generation
      const byteCharacters = atob(base64Audio);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'audio/mpeg' });
      const blobUrl = URL.createObjectURL(blob);

      setMp3Content(blobUrl);
      // Update generation key to force AudioPlayer to re-render
      setAudioGenerationKey(Date.now());
      setIsGenerating(false);
    } catch (error) {
      setIsGenerating(false);
      console.error('TTS generation error:', error);
    }
  };

  const handleDownload = async () => {
    let downloadFilename = fileName;
    if (downloadFilename.length === 0) downloadFilename = uuidv4();
    const href = mp3Content;
    if (!downloadFilename.endsWith('.mp3')) downloadFilename += '.mp3';
    // create "a" HTML element with href to file & click
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', downloadFilename);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    // Don't revoke blob URL here - user might want to play it again
    resetDefaults();
    //enqueueSnackbar('Audio generated and downloaded. Check your downloads folder', { variant: 'success' });
  };

  const handleTtsSuggestions = async (tone?: string) => {
    if (!apiKey || apiKey.length === 0) return false;

    setIsGeneratingSuggestion(true);
    setSuggestionError(null);
    setSuggestion(undefined);

    try {
      const openai = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true,
      });
      // Try gpt-4o first, fallback to gpt-4-turbo or gpt-3.5-turbo if access is denied
      // Note: gpt-4 (base) doesn't support json_object response_format
      const models = [
        { name: 'gpt-4o', supportsJsonObject: true },
        { name: 'gpt-4-turbo', supportsJsonObject: true },
        { name: 'gpt-4', supportsJsonObject: false },
        { name: 'gpt-3.5-turbo', supportsJsonObject: true },
      ];
      let lastError: any;

      for (const modelConfig of models) {
        try {
          // Build messages for this model attempt
          const messages: OpenAI.Chat.Completions.ChatCompletionMessageParam[] = [];
          messages.push({
            role: 'system',
            content: prompts.ttsSystem(),
          });
          if (tone === 'professional') {
            messages.push({
              role: 'system',
              content: prompts.professionalSystem(),
            });
          }

          // Add explicit JSON instruction if model doesn't support json_object
          const userContent = modelConfig.supportsJsonObject
            ? textInput
            : `${textInput}\n\nIMPORTANT: You must respond with valid JSON only. Return a JSON object with 'revised_text' and 'explanation' properties. Do not include any markdown formatting or code blocks, just the raw JSON.`;

          messages.push({
            role: 'user',
            content: userContent,
          });

          const requestOptions: OpenAI.Chat.Completions.ChatCompletionCreateParams = {
            model: modelConfig.name,
            messages: messages,
          };

          // Only add response_format for models that support it
          if (modelConfig.supportsJsonObject) {
            requestOptions.response_format = { type: 'json_object' };
          }

          const result = await openai.chat.completions.create(requestOptions);
          const content = result.choices[0].message.content || '{}';

          // Try to parse JSON, even if response_format wasn't used
          let parsedContent;
          try {
            parsedContent = JSON.parse(content);
          } catch (parseError) {
            // If parsing fails, try to extract JSON from markdown code blocks
            const jsonMatch = content.match(/```(?:json)?\s*(\{[\s\S]*\})\s*```/);
            if (jsonMatch) {
              parsedContent = JSON.parse(jsonMatch[1]);
            } else {
              throw new Error('Failed to parse JSON response from model');
            }
          }

          setSuggestion(parsedContent);
          setIsGeneratingSuggestion(false);
          return true;
        } catch (error: any) {
          lastError = error;
          // If it's a 403 (access denied), 404 (model not found), or 400 (invalid parameter), try next model
          if (error?.status === 403 || error?.status === 404 || error?.status === 400) {
            continue;
          }
          // For other errors, throw immediately
          throw error;
        }
      }

      // If all models failed, show error
      setSuggestionError(
        lastError?.message || 'Failed to generate suggestion. Please check your API key and try again.'
      );
      setIsGeneratingSuggestion(false);
      return false;
    } catch (error: any) {
      setSuggestionError(error?.message || 'An error occurred while generating suggestions. Please try again.');
      setIsGeneratingSuggestion(false);
      return false;
    }
  };

  return (
    <Box sx={{ p: 5 }}>
      <Card>
        <CardHeader
          title='Text-to-Speech'
          sx={{
            backgroundColor: colors.grey[100],
            borderBottom: `solid 1px ${colors.grey[200]}`,
            p: 6,
          }}
        />
        <CardContent>
          <Grid
            container
            spacing={5}
          >
            <Grid size={{ md: 6 }}>
              <Stack spacing={3}>
                <TextField
                  id='text_input'
                  key='text_input'
                  label='Text'
                  variant='outlined'
                  fullWidth
                  multiline
                  minRows={5}
                  maxRows={12}
                  value={textInput}
                  onChange={({ target }) => setTextInput(target.value)}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
                  <FormControl
                    key='voice'
                    fullWidth
                  >
                    <InputLabel>Voice</InputLabel>
                    <Select
                      id='voice'
                      fullWidth
                      value={voice}
                      onChange={({ target }) => setVoice(target.value)}
                    >
                      {voices.map((x) => (
                        <MenuItem
                          key={`voice_${x}`}
                          value={x}
                        >
                          {contentHelper.humanize(x)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {isGenerating ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Button
                      component='button'
                      onClick={handleGenerate}
                      variant='contained'
                      sx={{
                        width: '100%',
                      }}
                      disabled={isGenerating}
                    >
                      Generate
                    </Button>
                  )}
                </Box>
                <Box>
                  {apiKey && apiKey.length > 0 ? (
                    <Stack spacing={2}>
                      <Stack
                        direction='row'
                        spacing={3}
                      >
                        <Button
                          component='button'
                          onClick={() => handleTtsSuggestions('professional')}
                          variant='outlined'
                          size='small'
                          disabled={isGeneratingSuggestion}
                          loading={isGeneratingSuggestion}
                          loadingPosition='end'
                        >
                          Professional
                        </Button>
                        <Button
                          component='button'
                          onClick={() => handleTtsSuggestions()}
                          variant='outlined'
                          size='small'
                          disabled={isGeneratingSuggestion}
                          loading={isGeneratingSuggestion}
                          loadingPosition='end'
                        >
                          Optimize TTS
                        </Button>
                      </Stack>
                      {suggestionError && (
                        <Alert
                          severity='error'
                          onClose={() => setSuggestionError(null)}
                        >
                          {suggestionError}
                        </Alert>
                      )}
                    </Stack>
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
                      <TextField
                        id='api_key'
                        key='api_key'
                        label='Enter your OpenAI key to enable advanced functions'
                        variant='outlined'
                        fullWidth
                        value={apiKey}
                        onChange={({ target }) => setApiKey(target.value)}
                      />
                      <Box>
                        <Button
                          component='button'
                          onClick={handleDownload}
                          variant='outlined'
                          size='small'
                          sx={{
                            width: '100%',
                          }}
                        >
                          Save key
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
                {suggestion && (
                  <Card>
                    <CardContent>
                      {suggestion.revised_text ? (
                        <Stack spacing={2}>
                          <Stack
                            direction='row'
                            spacing={3}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              background: '#EEE',
                              borderRadius: '5px',
                              padding: '10px',
                            }}
                          >
                            <Stack
                              direction='row'
                              spacing={4}
                              sx={{ flexGrow: 1 }}
                            >
                              <Typography component='div'>{suggestion.revised_text}</Typography>
                            </Stack>
                            <Stack
                              direction='row'
                              spacing={4}
                              sx={{ justifySelf: 'end' }}
                            >
                              <CopyToClipboard text={suggestion.revised_text}>
                                <IconButton>
                                  <ContentCopy />
                                </IconButton>
                              </CopyToClipboard>
                            </Stack>
                          </Stack>
                          {suggestion.explanation && (
                            <Alert severity='info'>
                              <Typography variant='h6'>Explanation</Typography>
                              <Typography component='div'>
                                <ReactMarkdown>{suggestion.explanation.toString()}</ReactMarkdown>
                              </Typography>
                            </Alert>
                          )}
                        </Stack>
                      ) : (
                        <pre>{JSON.stringify(suggestion, null, 2)}</pre>
                      )}
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Grid>
            <Grid size={{ md: 6 }}>
              {mp3Content && mp3Content.length > 0 && (
                <Stack spacing={3}>
                  <AudioPlayer
                    key={`audio-${voice}-${audioGenerationKey}`}
                    content={mp3Content}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
                    <TextField
                      id='file_name'
                      key='file_name'
                      label='Download file name'
                      variant='outlined'
                      fullWidth
                      value={fileName}
                      onChange={({ target }) => setFileName(target.value)}
                      inputProps={{ maxLength: 32 }}
                    />
                    <Button
                      component='button'
                      onClick={handleDownload}
                      variant='contained'
                      sx={{
                        width: '100%',
                      }}
                    >
                      Download
                    </Button>
                  </Box>
                </Stack>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
